import { IndustryType } from "../codegen/graphql";
import type { TableColumnStorageConfig } from "../types";
import { IOT_COLUMN_FIELDS } from "./iotColumnFields";

// Asset list columns that are irrelevant for a brand and therefore removed
// entirely from the table config (columns panel, table, CSV export) — unlike
// defaultHidden columns, users cannot re-enable them. Applied to both the
// table's default columns and the export's CSV column defs, keyed by the
// logged-in user's industry type from the JWT.
export const REMOVED_FIELDS_BY_INDUSTRY: Record<IndustryType, string[]> = {
    // ARGOS: no IoT hardware columns and no condition/maintenance columns
    // (Condition through Next Service, inclusive)
    [IndustryType.SEMICONDUCTOR]: [
        "imei",
        "iotConnection",
        ...IOT_COLUMN_FIELDS,
        "harshCondition",
        "harshConditionScore",
        "exhaustFilter",
        "exhaustFilterConditionScore",
        "missingSensor",
        "lastMissingSensorValuesAt",
        "sensorValueOutOfRange",
        "lastSensorValueOutOfRangeAt",
        "sensorValueOutOfRangeParameter",
        "sensorValueOutOfRangeRecordedValue",
        "sensorValueOutOfRangeLowerThreshold",
        "sensorValueOutOfRangeUpperThreshold",
        "vsdCandidate",
        "vsdCandidateScore",
        "maintenance",
        "lastMaintenanceDate",
        "lastExhaustFilterChangeDate",
        "lastOilAndFilterChangeDate",
        "lastRemanufacturingDate",
        "daysUntilNextExhaustFilterChange",
        "daysUntilNextOilAndFilterChange",
        "daysUntilNextRemanufacturing",
        "daysUntilNextService",
    ],
    // OTTO: no semiconductor hierarchy columns
    [IndustryType.INDUSTRY]: ["semiconductorTool", "semiconductorChamber"],
};

export function filterColumnsForIndustry<T extends { field: string }>(
    columns: T[],
    industryType: IndustryType
): T[] {
    const removed = REMOVED_FIELDS_BY_INDUSTRY[industryType];
    if (!removed?.length) return columns;
    return columns.filter((col) => !removed.includes(col.field));
}

// Columns that only exist for one brand (see REMOVED_FIELDS_BY_INDUSTRY). When a saved
// config was last stamped under a different brand (or predates the feature and has no
// stamp), their saved visibility is dropped so they snap back to the current brand's
// default instead of carrying over.
export const BRAND_DEPENDENT_FIELDS = [
    "imei",
    "iotConnection",
    "semiconductorTool",
    "semiconductorChamber",
];

/**
 * The saved asset list config for the given brand: a semiconductor user immediately sees
 * Tool/Chamber (and no IMEI/IoT) without a manual reset, while every other column
 * customization is kept. The returned config is stamped with the brand.
 */
export const dropStaleBrandVisibility = (
    saved: TableColumnStorageConfig,
    industryType: IndustryType
): TableColumnStorageConfig => {
    if (saved.industryType === industryType) return saved;

    const columnVisibilityModel = { ...saved.columnVisibilityModel };
    BRAND_DEPENDENT_FIELDS.forEach((field) => delete columnVisibilityModel[field]);
    return { ...saved, columnVisibilityModel, industryType };
};
